import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eraser, Hand, Highlighter, LogOut, Minus, PenLine, StickyNote, Undo2, UsersRound } from "lucide-react";
import CollabBoard from "./CollabBoard";
import elumeLogo from "./assets/ELogo2.png";
import { selectStudentDrawingTool, studentBoardTool, type StudentDrawingTool } from "./collaborationToolControls";
import { ColourSwatch, StrokeSizeButton } from "./BoardControlOptions";
import { useUiLanguage } from "./i18n/UiLanguageContext";
import DemoParticipantLimitModal from "./DemoParticipantLimitModal";
import DemoParticipantInfoPanel from "./DemoParticipantInfoPanel";
import { isDemoParticipantLimitError, participantJoinError } from "./participantJoinError";

const API_BASE = "/api";
const STUDENT_NAME_KEY = "elume_student_name_v1";

type JoinResponse = {
  anon_id: string;
  name: string;
  room_number: number | null;
  participant_token: string;
};

type MeResponse = {
  id: number;
  anon_id: string;
  name: string;
  room_number: number | null;
  is_online: boolean;
  participant_token?: string;
  session_state: "lobby" | "assigning" | "live" | "review" | "ended";
};

type StatusResponse = {
  session_code: string;
  title: string;
  state: "lobby" | "assigning" | "live" | "review" | "ended";
  room_count: number;
  timer_minutes?: number | null;
  time_left_seconds?: number | null;
  board_round?: number;
  joined_count: number;
  assigned_count: number;
  is_demo?: boolean;
};

type ParticipantListItem = {
  id: number;
  name: string;
  room_number: number | null;
};

const STUDENT_PEN_COLOURS = [
  { value: "black", label: "Slate", dot: "bg-slate-900" },
  { value: "blue", label: "Blue", dot: "bg-blue-600" },
  { value: "green", label: "Emerald", dot: "bg-emerald-600" },
  { value: "cyan", label: "Cyan", dot: "bg-cyan-500" },
  { value: "purple", label: "Violet", dot: "bg-violet-600" },
  { value: "orange", label: "Orange", dot: "bg-orange-500" },
  { value: "red", label: "Red", dot: "bg-red-500" },
] as const;

const STUDENT_HIGHLIGHTER_COLOURS = [
  { value: "yellow", label: "Yellow", dot: "bg-yellow-300" },
  { value: "#86efac", label: "Mint", dot: "bg-emerald-300" },
  { value: "#7dd3fc", label: "Cyan", dot: "bg-cyan-300" },
  { value: "#f0abfc", label: "Pink", dot: "bg-fuchsia-300" },
] as const;

function cleanCode(s: string) {
  return s.replace(/[^A-Za-z0-9-_]/g, "").trim();
}

export default function StudentCollabRoomPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useUiLanguage();

  const sessionCode = cleanCode(code || "");
  const storageKey = useMemo(() => `elume:collab:participant:${sessionCode}`, [sessionCode]);

  const [nameInput, setNameInput] = useState("");
  const [anonId, setAnonId] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [roomNumber, setRoomNumber] = useState<number | null>(null);
  const [sessionState, setSessionState] = useState<StatusResponse["state"]>("lobby");
  const [boardRound, setBoardRound] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [demoParticipantLimitReached, setDemoParticipantLimitReached] = useState(false);
  const [isDemoSession, setIsDemoSession] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const [tool, setTool] = useState<StudentDrawingTool>("pen");
  const [viewportMode, setViewportMode] = useState<"fixed" | "pan">("fixed");
  const [penColor, setPenColor] = useState<(typeof STUDENT_PEN_COLOURS)[number]["value"]>("black");
  const [penSize, setPenSize] = useState<1 | 2 | 3>(1);
  const [highlighterColor, setHighlighterColor] = useState<(typeof STUDENT_HIGHLIGHTER_COLOURS)[number]["value"]>("yellow");
  const [roomMembers, setRoomMembers] = useState<ParticipantListItem[]>([]);
  const [membersExpanded, setMembersExpanded] = useState(false);
  const [studentUndo, setStudentUndo] = useState<(() => void) | null>(null);
  const [canStudentUndo, setCanStudentUndo] = useState(false);

  const handleParticipantUndoReady = useCallback((undo: () => void, canUndo: boolean) => {
    setStudentUndo(() => undo);
    setCanStudentUndo(canUndo);
  }, []);

  async function fetchStatus() {
    const res = await fetch(`${API_BASE}/collab/${sessionCode}/status`);
    if (!res.ok) throw new Error("Could not fetch session status");
    const data = (await res.json()) as StatusResponse;
    setSessionState(data.state);
    setBoardRound(Math.max(1, Number(data.board_round || 1)));
    setIsDemoSession(data.is_demo === true);
  }

  function storedParticipant() {
    try {
      const value = localStorage.getItem(storageKey);
      return value ? (JSON.parse(value) as JoinResponse) : null;
    } catch { return null; }
  }

  async function fetchMe(existingAnonId: string, token?: string): Promise<number | null> {
    const participantToken = token || storedParticipant()?.participant_token;
    if (!participantToken) throw new Error("Could not verify participant");
    const res = await fetch(`${API_BASE}/collab/${sessionCode}/me/${existingAnonId}?${new URLSearchParams({ participant_token: participantToken })}`);
    if (!res.ok) throw new Error("Could not fetch participant");
    const data = (await res.json()) as MeResponse;
    if (!data.is_online) {
      throw new Error("This participation is no longer active.");
    }
    const nextRoomNumber = data.room_number == null ? null : Number(data.room_number);
    setAnonId(data.anon_id);
    setParticipantName(data.name);
    setRoomNumber(nextRoomNumber);
    setSessionState(data.session_state);
    if (data.participant_token) {
      localStorage.setItem(storageKey, JSON.stringify({
        anon_id: data.anon_id,
        name: data.name,
        room_number: nextRoomNumber,
        participant_token: data.participant_token,
      }));
    }
    return nextRoomNumber;
  }

  async function fetchParticipants(targetRoomNumber?: number | null, credentials?: JoinResponse) {
    const participant = credentials || storedParticipant();
    if (!participant?.anon_id || !participant.participant_token) throw new Error("Could not verify participant");
    const query = new URLSearchParams({ anon_id: participant.anon_id, participant_token: participant.participant_token });
    const res = await fetch(`${API_BASE}/collab/${sessionCode}/room-participants?${query}`);
    if (!res.ok) throw new Error("Could not fetch participants");
    const data = await res.json();
    const list = Array.isArray(data?.participants) ? data.participants : [];
    const currentRoomNumber = targetRoomNumber == null ? roomNumber : Number(targetRoomNumber);
    setRoomMembers(
      list
        .map((p: any) => ({
          id: Number(p.id),
          name: String(p.name || "Student"),
          room_number: p.room_number == null ? null : Number(p.room_number),
        }))
        .filter((p: ParticipantListItem) => p.room_number !== null && p.room_number === currentRoomNumber)
    );
  }

  async function joinSession() {
    const clean = nameInput.trim();
    if (clean.length < 2) {
      setError("Please enter your name.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Reuse the opaque participant id held by this browser for this board.
      // This lets a participant recover after a refresh without taking another
      // place in a bounded demo (or normal) session.
      const existingParticipant = storedParticipant();
      const res = await fetch(`${API_BASE}/collab/${sessionCode}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: clean,
          anon_id: existingParticipant?.participant_token ? existingParticipant.anon_id : undefined,
        }),
      });

      if (!res.ok) {
        throw await participantJoinError(res, "Could not join session.");
      }

      const data = (await res.json()) as JoinResponse;
      localStorage.setItem(STUDENT_NAME_KEY, clean);
      localStorage.setItem(storageKey, JSON.stringify(data));

      setAnonId(data.anon_id);
      setParticipantName(data.name);
      setRoomNumber(data.room_number == null ? null : Number(data.room_number));

      await fetchStatus();
      const nextRoomNumber = await fetchMe(data.anon_id, data.participant_token);
      await fetchParticipants(nextRoomNumber, data);
    } catch (e: any) {
      if (isDemoParticipantLimitError(e)) setDemoParticipantLimitReached(true);
      else setError(e?.message || "Could not join session.");
    } finally {
      setLoading(false);
    }
  }

  function clearLocalParticipation() {
    localStorage.removeItem(storageKey);
    setAnonId("");
    setRoomNumber(null);
    setParticipantName("");
  }

  async function leaveSession() {
    if (!anonId || isLeaving) return;
    setIsLeaving(true);
    setError(null);
    try {
      const raw = localStorage.getItem(storageKey);
      const stored = raw ? (JSON.parse(raw) as JoinResponse) : null;
      if (!stored?.participant_token || stored.anon_id !== anonId) {
        throw new Error(t("collab.leaveUnavailable"));
      }
      const res = await fetch(`${API_BASE}/collab/${sessionCode}/leave`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anon_id: anonId, participant_token: stored.participant_token }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(detail || t("collab.leaveFailed"));
      }
      clearLocalParticipation();
      navigate("/student?mode=collab", { replace: true });
    } catch (leaveError: any) {
      setError(leaveError?.message || t("collab.leaveFailed"));
      setIsLeaving(false);
    }
  }

  useEffect(() => {
    if (!sessionCode) {
      setError("Missing session code.");
      setLoading(false);
      return;
    }

    const sharedName = (localStorage.getItem(STUDENT_NAME_KEY) || "").trim();
    if (sharedName) {
      setNameInput((prev) => prev || sharedName);
    }

    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      setLoading(false);
      return;
    }

    try {
      const parsed = JSON.parse(raw) as JoinResponse;
      if (!parsed?.anon_id) {
        setLoading(false);
        return;
      }

      fetchStatus()
        .then(() => fetchMe(parsed.anon_id))
        .then((latestRoomNumber) => fetchParticipants(latestRoomNumber))
        .catch(() => clearLocalParticipation())
        .finally(() => setLoading(false));
    } catch {
      setLoading(false);
    }
  }, [sessionCode, storageKey]);

  useEffect(() => {
    if (!anonId) return;

    const timer = window.setInterval(() => {
      fetchStatus().catch(() => {});
      fetchMe(anonId)
        .then((latestRoomNumber) => fetchParticipants(latestRoomNumber))
        .catch(() => clearLocalParticipation());
    }, 1500);

    return () => window.clearInterval(timer);
  }, [anonId, roomNumber, sessionCode]);

  useEffect(() => {
    if (!anonId || roomNumber === null) return;
    fetchParticipants(roomNumber).catch(() => {});
  }, [anonId, roomNumber, sessionCode]);

    useEffect(() => {
    if (sessionState !== "ended") return;

    const t = window.setTimeout(() => {
      navigate("/student?mode=collab", { replace: true });
    }, 1800);

    return () => window.clearTimeout(t);
  }, [sessionState, navigate]);

  if (!anonId && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-4">
        {demoParticipantLimitReached ? (
          <DemoParticipantLimitModal
            experience="collaboration"
            onBackToHub={() => navigate("/student?mode=collab", { replace: true })}
            onStartTrial={() => navigate("/register")}
          />
        ) : null}
        <div className="mx-auto max-w-md rounded-[32px] border border-white/70 bg-white/85 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.10)] backdrop-blur-xl">
          <div className="text-center">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-3xl border border-violet-200 bg-violet-100/80 text-violet-800 shadow-xl ring-1 ring-violet-200">
              <UsersRound aria-hidden="true" size={42} strokeWidth={2.2} />
            </div>

            <div className="mt-4 text-[11px] font-black uppercase tracking-[0.18em] text-violet-700">
              {t("collab.board")}
            </div>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900">
              {t("collab.joinHeading")}
            </h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {t("collab.joinGuidance")}
            </p>
          </div>

          <div className="mt-6 rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-4 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
              {t("collab.code")}
            </div>
            <div className="mt-2 text-2xl font-black tracking-[0.08em] text-slate-900">
              {sessionCode}
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-sm font-black text-slate-800">{t("collab.yourName")}</label>
            <input
              className="mt-2 w-full rounded-2xl border border-violet-200 bg-white px-4 py-4 text-base font-semibold text-slate-900 shadow-sm outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder={t("collab.namePlaceholder")}
            />
          </div>

          {error ? (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
              {error}
            </div>
          ) : null}

          <button
            type="button"
            onClick={joinSession}
            className="mt-5 w-full rounded-2xl bg-gradient-to-r from-violet-600 via-purple-500 to-fuchsia-500 px-5 py-4 text-base font-black text-white shadow-lg"
          >
            {t("collab.joinBoard")}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4">
        <div className="mx-auto max-w-md rounded-[32px] border border-white/70 bg-white/85 p-6 text-center shadow-[0_20px_60px_rgba(15,23,42,0.10)] backdrop-blur-xl">
          <div className="text-2xl font-black text-slate-900">Loading…</div>
        </div>
      </div>
    );
  }

    if (sessionState === "ended") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4">
        <div className="mx-auto max-w-md rounded-[32px] border border-white/70 bg-white/85 p-6 text-center shadow-[0_20px_60px_rgba(15,23,42,0.10)] backdrop-blur-xl">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl border border-slate-200 bg-slate-50 shadow-sm">
            <img src={elumeLogo} alt="Elume" className="h-10 w-10 object-contain" />
          </div>

          <div className="mt-5 text-2xl font-black text-slate-900">Session ended</div>
          <div className="mt-2 text-sm leading-6 text-slate-600">
            Your teacher has ended the collaboration session. Returning you to Student Hub…
          </div>

          <button
            type="button"
            onClick={() => navigate("/student?mode=collab", { replace: true })}
            className="mt-6 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-800 shadow-sm"
          >
            Return now
          </button>
        </div>
      </div>
    );
  }

  if (sessionState !== "live" || roomNumber === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4">
        <div className="mx-auto max-w-md rounded-[32px] border border-white/70 bg-white/85 p-6 text-center shadow-[0_20px_60px_rgba(15,23,42,0.10)] backdrop-blur-xl">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl border border-emerald-100 bg-emerald-50 shadow-sm">
            <img src={elumeLogo} alt="Elume" className="h-10 w-10 object-contain" />
          </div>

          <div className="mt-5 text-2xl font-black text-slate-900">Waiting…</div>
          <div className="mt-2 text-sm leading-6 text-slate-600">
            {roomNumber === null
              ? t("collab.roomNotAssigned")
              : t("collab.sessionNotStarted")}
          </div>

          <div className="mt-4 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-black text-slate-700 inline-flex">
            {participantName || "Student"}
          </div>

          {isDemoSession ? <DemoParticipantInfoPanel experience="collaboration" /> : null}

          {error ? <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-900">{error}</div> : null}

          <button
            type="button"
            onClick={leaveSession}
            disabled={isLeaving}
            className="mt-6 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-800 shadow-sm"
          >
            {isLeaving ? t("collab.leaving") : t("collab.leaveSession")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-3 md:p-4">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 rounded-[32px] border border-white/70 bg-white/85 p-4 shadow-[0_20px_60px_rgba(15,23,42,0.10)] backdrop-blur-xl">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/70 bg-white shadow-md">
                <img src={elumeLogo} alt="Elume" className="h-9 w-9 object-contain" />
              </div>
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700">
                  Collaboration Room
                </div>
                <div className="text-2xl font-black tracking-tight text-slate-900">
                  {t("collab.room").replace("{{number}}", String(roomNumber))}
                </div>
                <div className="mt-1 inline-flex rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-violet-700">
                  {t("collab.breakoutRoom").replace("{{number}}", String(roomNumber))}
                </div>
                <div className="text-sm text-slate-600">{participantName}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5 rounded-[24px] border border-slate-100 bg-slate-50/80 p-2">
              <button
                type="button"
                onClick={leaveSession}
                disabled={isLeaving}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-2xl border border-rose-200 bg-white px-3 py-2 text-xs font-black text-rose-800 shadow-sm transition hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 disabled:cursor-not-allowed disabled:text-rose-400"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" /> {isLeaving ? t("collab.leaving") : t("collab.leaveSession")}
              </button>
              <button
                type="button"
                onClick={() => selectStudentDrawingTool("pen", setTool, setViewportMode)}
                aria-pressed={tool === "pen"}
                className={`inline-flex min-h-11 items-center gap-1.5 rounded-2xl px-3 py-2 text-xs font-black shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                  tool === "pen" ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white ring-2 ring-emerald-200" : "border border-slate-200 bg-white text-slate-800 hover:bg-emerald-50"
                }`}
              >
                <PenLine className="h-4 w-4" aria-hidden="true" /> {t("collab.pen")}
              </button>
              <button
                type="button"
                onClick={() => selectStudentDrawingTool("highlighter", setTool, setViewportMode)}
                aria-pressed={tool === "highlighter"}
                className={`inline-flex min-h-11 items-center gap-1.5 rounded-2xl px-3 py-2 text-xs font-black shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                  tool === "highlighter"
                    ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white ring-2 ring-emerald-200"
                    : "border border-slate-200 bg-white text-slate-800 hover:bg-emerald-50"
                }`}
              >
                <Highlighter className="h-4 w-4" aria-hidden="true" /> {t("collab.highlighter")}
              </button>
              <button
                type="button"
                onClick={() => selectStudentDrawingTool("line", setTool, setViewportMode)}
                aria-pressed={tool === "line"}
                className={`inline-flex min-h-11 items-center gap-1.5 rounded-2xl px-3 py-2 text-xs font-black shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                  tool === "line"
                    ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white ring-2 ring-emerald-200"
                    : "border border-slate-200 bg-white text-slate-800 hover:bg-emerald-50"
                }`}
              >
                <Minus className="h-4 w-4" aria-hidden="true" /> {t("collab.line")}
              </button>
              <button
                type="button"
                onClick={() => selectStudentDrawingTool("eraser", setTool, setViewportMode)}
                aria-pressed={tool === "eraser"}
                className={`inline-flex min-h-11 items-center gap-1.5 rounded-2xl px-3 py-2 text-xs font-black shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                  tool === "eraser"
                    ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white ring-2 ring-emerald-200"
                    : "border border-slate-200 bg-white text-slate-800 hover:bg-emerald-50"
                }`}
              >
                <Eraser className="h-4 w-4" aria-hidden="true" /> {t("collab.eraser")}
              </button>
              <button
                type="button"
                onClick={() => selectStudentDrawingTool("sticky", setTool, setViewportMode)}
                aria-pressed={tool === "sticky"}
                className={`inline-flex min-h-11 items-center gap-1.5 rounded-2xl px-3 py-2 text-xs font-black shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                  tool === "sticky"
                    ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white ring-2 ring-violet-200"
                    : "border border-slate-200 bg-white text-slate-800 hover:bg-violet-50"
                }`}
              >
                <StickyNote className="h-4 w-4" aria-hidden="true" /> {t("collab.stickyNote")}
              </button>
              <button
                type="button"
                onClick={() => studentUndo?.()}
                disabled={!canStudentUndo}
                className={`inline-flex min-h-11 items-center gap-1.5 rounded-2xl px-3 py-2 text-xs font-black shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${
                  canStudentUndo
                    ? "border border-slate-200 bg-white text-slate-800 hover:bg-violet-50"
                    : "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400"
                }`}
              >
                <Undo2 className="h-4 w-4" aria-hidden="true" /> {t("collab.undo")}
              </button>
              <button
                type="button"
                onClick={() => setViewportMode((prev) => (prev === "pan" ? "fixed" : "pan"))}
                role="switch"
                aria-checked={viewportMode === "pan"}
                className={`inline-flex min-h-11 items-center gap-2 rounded-2xl px-3 py-2 text-xs font-black shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${
                  viewportMode === "pan"
                    ? "bg-cyan-600 text-white"
                    : "border border-slate-200 bg-white text-slate-800"
                } md:hidden`}
              >
                <Hand className="h-4 w-4" aria-hidden="true" />
                {viewportMode === "pan" ? t("collab.moveBoardOn") : t("collab.moveBoard")}
                <span className={`relative h-5 w-9 rounded-full ${viewportMode === "pan" ? "bg-white/80" : "bg-slate-300"}`} aria-hidden="true"><span className={`absolute top-0.5 h-4 w-4 rounded-full bg-slate-700 transition-transform ${viewportMode === "pan" ? "translate-x-4" : "translate-x-0.5"}`} /></span>
              </button>
              {viewportMode === "pan" && <div className="w-full rounded-xl bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-800 md:hidden">{t("collab.moveBoardGuidance")}</div>}
              {(tool === "pen" || tool === "line") && (
                <div className="ml-1 flex flex-wrap items-center gap-1 rounded-2xl border border-slate-200 bg-white px-2 py-2 shadow-sm">
                  <span className="px-2 text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">{t("collab.colour")}</span>
                  {STUDENT_PEN_COLOURS.map((option) => (
                    <ColourSwatch
                      key={option.value}
                      color={option.value}
                      label={option.label}
                      selected={penColor === option.value}
                      onClick={() => setPenColor(option.value)}
                    />
                  ))}
                </div>
              )}
              {tool === "highlighter" && (
                <div className="ml-1 flex flex-wrap items-center gap-1 rounded-2xl border border-slate-200 bg-white px-2 py-2 shadow-sm">
                  <span className="px-2 text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">{t("collab.highlight")}</span>
                  {STUDENT_HIGHLIGHTER_COLOURS.map((option) => (
                    <ColourSwatch
                      key={option.value}
                      color={option.value}
                      label={option.label}
                      selected={highlighterColor === option.value}
                      onClick={() => setHighlighterColor(option.value)}
                    />
                  ))}
                </div>
              )}
              {(tool === "pen" || tool === "line" || tool === "highlighter") && <div className="ml-1 inline-flex items-center gap-1 rounded-2xl border border-slate-200 bg-white px-2 py-2 shadow-sm">
                <span className="px-2 text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">{t("collab.stroke")}</span>
                {([{ value: 1 }, { value: 2 }, { value: 3 }] as const).map(({ value }) => (
                  <StrokeSizeButton
                    key={value}
                    value={value}
                    color={tool === "highlighter" ? highlighterColor : penColor}
                    selected={penSize === value}
                    onClick={() => setPenSize(value)}
                  />
                ))}
              </div>}
            </div>
          </div>
          {error ? <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-900">{error}</div> : null}
        </div>

        <div className="relative">
          <div className={`absolute right-3 top-3 z-20 rounded-[24px] border border-white/70 bg-white/80 shadow-lg backdrop-blur-xl max-md:top-2 ${membersExpanded ? "w-52 p-3" : "p-1.5"}`}>
            <div className="flex items-center justify-between gap-2">
              {membersExpanded && <div className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">{t("collab.roomMembers")}</div>}
              <button
                type="button"
                onClick={() => setMembersExpanded((prev) => !prev)}
                aria-expanded={membersExpanded}
                aria-controls="student-room-members"
                className="inline-flex items-center gap-1.5 rounded-2xl border border-cyan-200 bg-white px-3 py-2 text-[11px] font-black text-cyan-800 shadow-sm hover:bg-cyan-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-100"
              >
                <UsersRound size={14} aria-hidden="true" />
                {membersExpanded ? "Close" : `Room members · ${roomMembers.length}`}
              </button>
            </div>

            {membersExpanded && (
              <div id="student-room-members" className="mt-3 space-y-2">
                {roomMembers.length ? (
                  roomMembers.map((member) => (
                    <div key={member.id} className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-2 text-sm font-semibold text-slate-800">
                      {member.name}
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-white/70 px-3 py-2 text-xs text-slate-500">
                    Room list updates when available.
                  </div>
                )}
              </div>
            )}
          </div>

          <CollabBoard
            sessionCode={sessionCode}
            roomKey={`room-${roomNumber}`}
            boardRound={boardRound}
            participantId={anonId}
            participantAnonId={anonId}
            tool={studentBoardTool(viewportMode, tool)}
            penColor={penColor}
            penSize={penSize}
            highlighterColor={highlighterColor}
            eraserSize={2}
            height={760}
            viewportMode={viewportMode}
            boardWidth={1600}
            boardHeight={1200}
            onParticipantUndoReady={handleParticipantUndoReady}
          />
        </div>
      </div>
    </div>
  );
}
